import c from 'classnames';
import React, { useCallback, useEffect, useState } from 'react';
import { useToasts } from 'react-toast-notifications';
import PricingPageComponent from '../../../common/PricingPageComponent';
import BlockNavigation from '../../../components/BlockNavigation';
import CreatePageButton from '../../../components/CreatePageButton';
import EmptyState from '../../../components/EmptyState';
import { StyledForm } from '../../../components/Form';
import Loader from '../../../components/Loader';
import { ROUTES } from '../../../routes/routes';
import { CustomerPortal } from '../../../types/customerPortal';
import { PricingPage } from '../../../types/pricingPage';
import { useAxios } from '../../../utils/hooks';
import Footer from '../../PricingPages/NewPage/Footer';

interface Props {
    customerPortal?: CustomerPortal;
    onChangePricing: (page: PricingPage) => void;
}

export default function ChoosePricing({ customerPortal, onChangePricing }: Props) {
    const { addToast } = useToasts();
    const axios = useAxios();
    const [pages, setPages] = useState<PricingPage[]>();
    const [selectedPage, _setSelectedPage] = useState<PricingPage | null>(null);
    const setSelectedPage = useCallback(
        (page: PricingPage) => {
            _setSelectedPage(page);
            onChangePricing(page);
        },
        [onChangePricing, _setSelectedPage]
    );
    useEffect(() => {
        if (!axios.current) {
            return;
        }

        axios.current
            .get<PricingPage[]>('/pricing-pages')
            .then((res) => {
                setPages(
                    res.data
                        .filter((page) => page.plans.length > 0)
                        .filter((page) => page.plans.some((plan) => plan.productType === 'recurring'))
                );
                if (customerPortal) {
                    const page = res.data.find((page) => page.id === customerPortal.pricingPageId);
                    if (page) {
                        //pre-select the page
                        _setSelectedPage(page);
                    }
                } else if (res.data.length === 1) {
                    setSelectedPage(res.data[0]);
                }
            })
            .catch((err) =>
                addToast('Something went wrong. Please reload the page and try again.', { appearance: 'error' })
            );
    }, [axios, addToast, setSelectedPage, customerPortal]);

    const handleSelectionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.currentTarget.checked) {
            const page = pages?.find((page) => page.id === e.currentTarget.value);
            if (!page) {
                console.error('Selected pricing page but ');
            } else {
                setSelectedPage(page);
            }
        }
    };

    return (
        <div className="grid grid-cols-1 gap-2">
            <h1 className="font-bold">Choose a Pricing Page to create a Customer Portal for:</h1>

            <StyledForm className="w-full">
                {!pages && <Loader />}
                {pages && pages.length === 0 && (
                    <EmptyState
                        body={
                            <div className="md:w-1/2 m-auto py-4">
                                <h4 className="text-xl">No Pages Found</h4>
                                <span>You can create a new pricing page by clicking the button below</span>
                                <div className="md:w-48 m-auto mt-6">
                                    <CreatePageButton />
                                </div>
                            </div>
                        }
                    />
                )}
                {pages &&
                    pages.length > 0 &&
                    pages.map((page) => (
                        <div className="flex w-full px-8" key={page.id}>
                            <div className="p-2 w-full">
                                <label
                                    className={c('h-full flex items-center border-gray-200 border p-4 rounded-lg', {
                                        'bg-wedgewood-200 border-wedgewood-400': page.id === selectedPage?.id,
                                    })}
                                >
                                    <input
                                        type="radio"
                                        className="form-radio text-wedgewood-600 shadow-sm focus:border-wedgewood-300 focus:ring focus:ring-offset-0 focus:ring-wedgewood-200 focus:ring-opacity-50"
                                        name="pricingPage"
                                        value={page.id}
                                        onChange={handleSelectionChange}
                                        checked={selectedPage?.id === page.id}
                                    />
                                    <div className="flex-grow">
                                        <h2 className="text-gray-900 title-font font-medium inline ml-4">
                                            {page.name}
                                        </h2>
                                    </div>
                                    <div className="overflow-x-hidden mx-4 scale-75">
                                        <PricingPageComponent page={page} disabled emptyContent={() => <></>} />
                                    </div>
                                </label>
                            </div>
                        </div>
                    ))}
            </StyledForm>

            {customerPortal && (
                <Footer
                    next={{ href: ROUTES.CUSTOMER_PORTALS_CONFIG, disabled: !selectedPage }}
                    item={customerPortal}
                />
            )}
            <BlockNavigation when={!selectedPage} />
        </div>
    );
}
